import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { queryKeys } from '@/lib/api/query-keys';
import type { UploadFn } from '@/lib/upload';
import { createPresignedUploader } from '@/lib/upload';

import {
  devicesResource,
  getDevicePresignedUrl,
  listMetaCategories,
  listMetaComponents,
} from './api';

/** Two presigned uploaders (images + installation-guide), memoised per mount. The server
 *  derives the storage path from the `attachmentType` passed to the presign request. */
export function useDeviceUploaders(): { imageUploader: UploadFn; guideUploader: UploadFn } {
  return useMemo(
    () => ({
      imageUploader: createPresignedUploader({
        getPresignedUrl: (file, ctx) => getDevicePresignedUrl(file, 'image', ctx),
      }),
      guideUploader: createPresignedUploader({
        getPresignedUrl: (file, ctx) => getDevicePresignedUrl(file, 'manual', ctx),
      }),
    }),
    [],
  );
}

/** The option lists are keyed under the entity they list (`queryKeys`), so a Category or
 *  Component write invalidates them; the Components feature reads the same two keys. */
export const deviceKeys = {
  ...devicesResource.keys,
  metaCategories: () => queryKeys.categories.meta('products', { status: 'active' }),
  metaComponents: () => queryKeys.components.meta('products', { status: 'active' }),
};

/** Active categories for the Device and Bundle forms' Category dropdown and the listing filters. */
export function useMetaCategories() {
  return useQuery({
    queryKey: deviceKeys.metaCategories(),
    queryFn: listMetaCategories,
    staleTime: 5 * 60 * 1000,
    // Feeds a save: re-read on every form open so another admin's write is never offered stale.
    refetchOnMount: 'always',
  });
}

/** Active components for the Device form's Applicable Components picker and the listing filter. */
export function useMetaComponents() {
  return useQuery({
    queryKey: deviceKeys.metaComponents(),
    queryFn: listMetaComponents,
    staleTime: 5 * 60 * 1000,
    // Feeds a save: re-read on every form open so another admin's write is never offered stale.
    refetchOnMount: 'always',
  });
}

/** Device CRUD query hooks. Created once by the shared catalog-resource factory; the
 *  listing query key is prefixed `['devices']`, so every write invalidates and refreshes it. */
export const useDevice = devicesResource.useDetail;
export const useCreateDevice = devicesResource.useCreate;
export const useUpdateDevice = devicesResource.useUpdate;
export const useSetDeviceStatus = devicesResource.useSetStatus;
export const useDeleteDevice = devicesResource.useDelete;
